import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { X, Globe, PenLine, Loader2, CheckCircle, AlertCircle, Search, Check } from 'lucide-react';
import { useFundraiserStore } from '../../store/fundraiserStore';
import { useOrganizationStore } from '../../store/organizationStore';
import { getOrganizationSports, fundraiserScreenshotsApi } from '../../lib/api';
import { formatSportLabel } from '../../lib/sportLabel';
import { detectSportMismatch } from '../../lib/fundraiserSportMatch';
import { ScreenshotPicker } from '../competitors/ScreenshotPicker';
import { screenshotsEnabled } from '../../lib/features';
import { downscaleImage } from '../../lib/imageDownscale';
import toast from 'react-hot-toast';
import type { OrganizationSearchResult, OrgSportOption } from '../../types';

interface CommonProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

// The org-page flow (SportsTeamsSection) always knows the org + sport already.
interface OrgContextProps extends CommonProps {
  organizationId: string;
  organizationName: string;
  sportId: string;
  sportDisplayName: string;
  /** Every sport the school has (hidden ones included) — powers the wrong-sport warning. */
  sports?: OrgSportOption[];
}

// The Competitors-page "easy add" flow has neither — the rep picks a school
// (and optionally a sport) inside the modal itself.
interface PickerModeProps extends CommonProps {
  organizationId?: undefined;
  organizationName?: undefined;
  sportId?: undefined;
  sportDisplayName?: undefined;
  sports?: undefined;
}

type AddFundraiserModalProps = OrgContextProps | PickerModeProps;

// A proper type guard (rather than an aliased boolean) so every access site
// narrows reliably — including through ternaries and object-literal fields,
// not just an `if` block.
function isOrgContextProps(props: AddFundraiserModalProps): props is OrgContextProps {
  return 'organizationId' in props;
}

const PLATFORM_OPTIONS = [
  { value: 'VERTICAL_RAISE', label: 'Vertical Raise' },
  { value: 'SNAP_RAISE', label: 'Snap Raise' },
  { value: 'ETEAM_SPONSOR', label: 'ETeamSponsor' },
  { value: 'SCHOOL_FUNDR', label: 'SchoolFundr' },
];

const STATUS_OPTIONS = [
  { value: 'UNKNOWN', label: 'Unknown' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'ENDED', label: 'Ended' },
];

const currentYear = new Date().getFullYear();

/** "Lincoln High — Austin, TX" — never a naked "Austin, TX" with no name. */
function formatSchoolResult(org: { name: string; city: string | null; state: string | null }): string {
  const location = [org.city, org.state].filter(Boolean).join(', ');
  return location ? `${org.name} — ${location}` : org.name;
}

export const AddFundraiserModal = (props: AddFundraiserModalProps) => {
  const { isOpen, onClose, onSuccess } = props;
  // Distinguishing on the presence of the org-context props (not their
  // truthiness) is what keeps this reliable — the picker-mode variant simply
  // never has the key, rather than the key holding an empty/falsy value.
  const isOrgMode = isOrgContextProps(props);

  const { createFundraiser, extractFromUrl } = useFundraiserStore();
  const { searchResults, isSearching, searchOrganizations, clearSearchResults } = useOrganizationStore();

  const [activeTab, setActiveTab] = useState<'url' | 'manual'>('url');
  const [isSaving, setIsSaving] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractUiState, setExtractUiState] = useState<'idle' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  // Form fields
  const [url, setUrl] = useState('');
  const [platform, setPlatform] = useState('');
  const [year, setYear] = useState(currentYear);
  const [raisedAmount, setRaisedAmount] = useState('');
  const [goalAmount, setGoalAmount] = useState('');
  const [status, setStatus] = useState('UNKNOWN');
  const [campaignName, setCampaignName] = useState('');
  const [campaignUrl, setCampaignUrl] = useState('');
  const [extractedSport, setExtractedSport] = useState<string | null>(null);
  const [screenshotFiles, setScreenshotFiles] = useState<File[]>([]);

  // Wrong-sport warning: an org-mode switch away from the clicked sport, and
  // the detected label the rep chose to keep anyway.
  const [sportOverrideId, setSportOverrideId] = useState<string | null>(null);
  const [dismissedMismatch, setDismissedMismatch] = useState<string | null>(null);

  // Picker-mode-only state: school search + optional sport
  const [schoolQuery, setSchoolQuery] = useState('');
  const [selectedOrg, setSelectedOrg] = useState<OrganizationSearchResult | null>(null);
  const [orgSports, setOrgSports] = useState<OrgSportOption[]>([]);
  const [isLoadingSports, setIsLoadingSports] = useState(false);
  const [selectedSportId, setSelectedSportId] = useState<string | null>(null);
  // Monotonically increasing id for the in-flight sports fetch — a newer
  // fetch's result only applies if its id is still the latest one issued.
  // Keying by orgId isn't enough: select A, Change, then re-select A gives
  // two in-flight requests with the SAME orgId, and an id-based check can't
  // tell the abandoned one from the current one; a counter can.
  const sportsRequestIdRef = useRef(0);

  useEffect(() => {
    if (isOpen) {
      setActiveTab('url');
      setUrl('');
      setPlatform('');
      setYear(currentYear);
      setRaisedAmount('');
      setGoalAmount('');
      setStatus('UNKNOWN');
      setCampaignName('');
      setCampaignUrl('');
      setExtractedSport(null);
      setScreenshotFiles([]);
      setSportOverrideId(null);
      setDismissedMismatch(null);
      setError(null);
      setExtractUiState('idle');
      setSchoolQuery('');
      setSelectedOrg(null);
      setOrgSports([]);
      setSelectedSportId(null);
      sportsRequestIdRef.current += 1; // invalidate any request still in flight
    } else if (!isOrgMode) {
      // Org-context mode never touches the organization search store — only
      // reach into it when this instance is actually the picker.
      clearSearchResults();
    }
  }, [isOpen, isOrgMode, clearSearchResults]);

  // Debounced school typeahead (picker mode only)
  useEffect(() => {
    if (isOrgMode || selectedOrg) return;
    const q = schoolQuery.trim();
    if (q.length < 2) {
      clearSearchResults();
      return;
    }
    const timer = setTimeout(() => searchOrganizations(q), 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolQuery, selectedOrg, isOrgMode]);

  const loadSportsForOrg = async (orgId: string) => {
    const requestId = ++sportsRequestIdRef.current;
    setIsLoadingSports(true);
    try {
      const sports = await getOrganizationSports(orgId);
      if (sportsRequestIdRef.current !== requestId) return; // a newer request raced ahead
      setOrgSports(sports ?? []);
    } catch {
      if (sportsRequestIdRef.current === requestId) setOrgSports([]);
    } finally {
      if (sportsRequestIdRef.current === requestId) setIsLoadingSports(false);
    }
  };

  const handleSelectSchool = (org: OrganizationSearchResult) => {
    setSelectedOrg(org);
    setSchoolQuery('');
    clearSearchResults();
    setSelectedSportId(null);
    setOrgSports([]);
    void loadSportsForOrg(org.id);
  };

  const handleChangeSchool = () => {
    setSelectedOrg(null);
    setSelectedSportId(null);
    setOrgSports([]);
    sportsRequestIdRef.current += 1; // invalidate any request still in flight
  };

  const handleExtract = async () => {
    if (!url.trim()) return;

    setIsExtracting(true);
    setExtractUiState('idle');
    setError(null);

    try {
      const { extraction, extractionStatus } = await extractFromUrl(url.trim());
      if (extraction.platform) setPlatform(extraction.platform);
      if (extraction.year) setYear(extraction.year);
      if (extraction.raisedAmount) setRaisedAmount(String(extraction.raisedAmount));
      if (extraction.goalAmount) setGoalAmount(String(extraction.goalAmount));
      if (extraction.campaignName) setCampaignName(extraction.campaignName);
      if (extraction.status) setStatus(extraction.status);
      setExtractedSport(extraction.sport ?? null);
      setExtractUiState(extractionStatus === 'ok' ? 'success' : 'error');
    } catch {
      setExtractUiState('error');
    } finally {
      setIsExtracting(false);
    }
  };

  const sportOptions: OrgSportOption[] = isOrgContextProps(props) ? props.sports ?? [] : orgSports;
  const chosenSportId = isOrgContextProps(props) ? sportOverrideId ?? props.sportId : selectedSportId;
  const overrideSport = sportOverrideId ? sportOptions.find((s) => s.id === sportOverrideId) : undefined;
  const chosenSportLabel = isOrgContextProps(props)
    ? overrideSport ? formatSportLabel(overrideSport.gender, overrideSport.name) : props.sportDisplayName
    : (() => {
        const s = orgSports.find((o) => o.id === selectedSportId);
        return s ? formatSportLabel(s.gender, s.name) : '';
      })();
  const mismatch = detectSportMismatch({
    chosenSportId,
    sports: sportOptions,
    extractedSport,
    campaignName,
  });
  const showMismatch = mismatch !== null && mismatch.detectedLabel !== dismissedMismatch;

  const handleSwitchSport = (sportId: string) => {
    if (isOrgMode) setSportOverrideId(sportId);
    else setSelectedSportId(sportId);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isOrgMode && !selectedOrg) {
      setError('Select a school first');
      return;
    }
    if (!platform) {
      setError('Platform is required');
      return;
    }

    // URL tab always carries the pasted URL forward, whether or not Extract
    // was ever pressed or it failed; manual tab carries its own field. These
    // are independent states — never fall back from one to the other here.
    const finalCampaignUrl = activeTab === 'url' ? url.trim() : campaignUrl.trim();
    const organizationId = isOrgContextProps(props) ? props.organizationId : selectedOrg!.id;
    const sportId = chosenSportId || undefined;

    setIsSaving(true);
    setError(null);

    try {
      const created = await createFundraiser({
        organizationId,
        sportId,
        platform,
        year,
        campaignUrl: finalCampaignUrl || null,
        campaignName: campaignName.trim() || null,
        raisedAmount: raisedAmount ? parseFloat(raisedAmount) : null,
        goalAmount: goalAmount ? parseFloat(goalAmount) : null,
        status,
      });

      if (screenshotsEnabled() && screenshotFiles.length > 0) {
        try {
          const blobs = await Promise.all(screenshotFiles.map((f) => downscaleImage(f)));
          await fundraiserScreenshotsApi.upload(created.id, blobs);
        } catch (err) {
          console.error('Screenshot upload failed', err);
          toast.error("The fundraiser was added, but the screenshot didn't upload. You can add it from Edit.");
        }
      }

      // Single-toast rule: this modal is the only place that toasts on a
      // successful add. Org-page mode keeps its exact original text; picker
      // mode composes from the values the rep just entered/picked rather than
      // the created record, so it reads the same regardless of what the API
      // echoes back.
      toast.success(
        isOrgMode ? 'Fundraiser added' : `Added — ${campaignName.trim() || 'fundraiser'} at ${selectedOrg!.name}`,
      );
      onSuccess();
    } catch (err: unknown) {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;
      const message = axios.isAxiosError(err) ? err.response?.data?.message : undefined;
      if (status === 409) {
        setError('A fundraiser with this URL already exists');
      } else {
        setError(typeof message === 'string' && message ? message : 'Failed to create fundraiser');
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Add Fundraiser
              </h2>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {isOrgContextProps(props) ? (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Adding fundraiser to <span className="font-medium text-gray-900 dark:text-gray-100">{props.organizationName}</span> — <span className="font-medium text-gray-900 dark:text-gray-100">{chosenSportLabel}</span>
                </p>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label htmlFor="add-fundraiser-school" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      School <span className="text-red-500">*</span>
                    </label>
                    {selectedOrg ? (
                      <div className="flex items-center justify-between bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <Check className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {formatSchoolResult(selectedOrg)}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={handleChangeSchool}
                          className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex-shrink-0"
                        >
                          Change
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <input
                            id="add-fundraiser-school"
                            type="text"
                            value={schoolQuery}
                            onChange={(e) => setSchoolQuery(e.target.value)}
                            className="input pl-9"
                            placeholder="Search schools..."
                            autoFocus
                          />
                          {isSearching && (
                            <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
                          )}
                        </div>
                        {searchResults.length > 0 && (
                          <div className="max-h-40 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                            {searchResults.map((org) => (
                              <button
                                key={org.id}
                                type="button"
                                onClick={() => handleSelectSchool(org)}
                                className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 text-sm text-gray-900 dark:text-gray-100 border-b border-gray-100 dark:border-gray-700 last:border-0"
                              >
                                {formatSchoolResult(org)}
                              </button>
                            ))}
                          </div>
                        )}
                        {searchResults.length === 0 && !isSearching && schoolQuery.trim().length >= 2 && (
                          <p className="text-xs text-gray-400 text-center py-1">No schools found</p>
                        )}
                      </div>
                    )}
                  </div>

                  {selectedOrg && (
                    <div>
                      <label htmlFor="add-fundraiser-sport" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Sport
                      </label>
                      {isLoadingSports ? (
                        <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                          <Loader2 className="h-4 w-4 animate-spin" /> Loading sports...
                        </div>
                      ) : (
                        <select
                          id="add-fundraiser-sport"
                          value={selectedSportId ?? ''}
                          onChange={(e) => setSelectedSportId(e.target.value || null)}
                          className="input"
                        >
                          <option value="">Not sure — leave blank</option>
                          {orgSports.map((s) => (
                            <option key={s.id} value={s.id}>{formatSportLabel(s.gender, s.name)}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}
                </div>
              )}

              {error && (
                <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-3 text-red-800 dark:text-red-300 text-sm">
                  {error}
                </div>
              )}

              {/* Tabs */}
              <div className="flex border-b border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setActiveTab('url')}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'url'
                      ? 'border-blast-600 text-blast-600 dark:border-blast-400 dark:text-blast-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  <Globe className="h-4 w-4" />
                  Paste URL
                </button>
                <button
                  type="button"
                  onClick={() => {
                    // Switching away from a pasted URL must not silently drop it.
                    if (!campaignUrl.trim() && url.trim()) setCampaignUrl(url.trim());
                    setActiveTab('manual');
                  }}
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'manual'
                      ? 'border-blast-600 text-blast-600 dark:border-blast-400 dark:text-blast-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  <PenLine className="h-4 w-4" />
                  Manual Entry
                </button>
              </div>

              {/* Tab content */}
              {activeTab === 'url' && (
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={url}
                      onChange={(e) => { setUrl(e.target.value); setExtractUiState('idle'); setExtractedSport(null); }}
                      className="input flex-1"
                      placeholder="https://app.verticalraise.com/fundraiser/..."
                      disabled={isExtracting}
                    />
                    <button
                      type="button"
                      onClick={handleExtract}
                      disabled={!url.trim() || isExtracting}
                      className="btn-primary whitespace-nowrap"
                    >
                      {isExtracting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        'Fetch & Extract'
                      )}
                    </button>
                  </div>
                  {extractUiState === 'success' && (
                    <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 rounded-lg p-2">
                      <CheckCircle className="h-4 w-4 flex-shrink-0" />
                      Extracted data from page — review and save below.
                    </div>
                  )}
                  {extractUiState === 'error' && (
                    <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded-lg p-2">
                      <AlertCircle className="h-4 w-4 flex-shrink-0" />
                      Couldn't read the fundraiser page — fill in the details manually.
                    </div>
                  )}
                </div>
              )}

              {/* Shared fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Platform <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={platform}
                    onChange={(e) => setPlatform(e.target.value)}
                    className="input"
                    disabled={isSaving}
                  >
                    <option value="">Select platform...</option>
                    {PLATFORM_OPTIONS.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Year <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={year}
                    onChange={(e) => setYear(parseInt(e.target.value) || currentYear)}
                    className="input"
                    min={currentYear - 5}
                    max={currentYear + 2}
                    disabled={isSaving}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Amount Raised
                  </label>
                  <input
                    type="number"
                    value={raisedAmount}
                    onChange={(e) => setRaisedAmount(e.target.value)}
                    className="input"
                    placeholder="0"
                    min={0}
                    step="0.01"
                    disabled={isSaving}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Goal Amount
                  </label>
                  <input
                    type="number"
                    value={goalAmount}
                    onChange={(e) => setGoalAmount(e.target.value)}
                    className="input"
                    placeholder="0"
                    min={0}
                    step="0.01"
                    disabled={isSaving}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Status
                </label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="input"
                  disabled={isSaving}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Fundraiser Name
                </label>
                <input
                  type="text"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  className="input"
                  placeholder="e.g., 2026 Spring Football Fundraiser"
                  disabled={isSaving}
                />
              </div>

              {screenshotsEnabled() && (
                <ScreenshotPicker files={screenshotFiles} onChange={setScreenshotFiles} disabled={isSaving} />
              )}

              {activeTab === 'manual' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Fundraiser URL
                  </label>
                  <input
                    type="url"
                    value={campaignUrl}
                    onChange={(e) => { setCampaignUrl(e.target.value); setExtractedSport(null); }}
                    className="input"
                    placeholder="https://..."
                    disabled={isSaving}
                  />
                </div>
              )}

              {showMismatch && mismatch && (
                <div
                  role="alert"
                  className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 space-y-2"
                >
                  <div className="flex items-start gap-2 text-sm text-amber-800 dark:text-amber-300">
                    <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span>
                      This looks like a <span className="font-medium">{mismatch.detectedLabel}</span> fundraiser, but you're adding it to <span className="font-medium">{chosenSportLabel}</span>.
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2 pl-6">
                    {mismatch.suggestion && (
                      <button
                        type="button"
                        onClick={() => handleSwitchSport(mismatch.suggestion!.id)}
                        className="btn-primary text-sm"
                      >
                        Switch to {formatSportLabel(mismatch.suggestion.gender, mismatch.suggestion.name)}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setDismissedMismatch(mismatch.detectedLabel)}
                      className="btn-secondary text-sm"
                    >
                      Keep {chosenSportLabel}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={isSaving || !platform || (!isOrgMode && !selectedOrg)}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  'Add Fundraiser'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
